/**
 * Gap-clustered observation freshness for the cheapest-itineraries table.
 *
 * No React/JSX imports — this module is imported directly by server-side
 * unit tests via relative path.
 */

export const RUN_GAP_MS = 30 * 60_000;

export interface RunFreshness {
  /** Newest observation in the payload — the "last run" timestamp shown in the caption. */
  lastRunAt: string | null;
  /** Oldest observation belonging to the newest run cluster. Rows at or after this are fresh. */
  cutoffAt: string | null;
  refreshed: number;
  total: number;
}

export function deriveRunFreshness(
  observedAts: string[],
  gapMs: number = RUN_GAP_MS,
): RunFreshness {
  const total = observedAts.length;
  if (total === 0) {
    return { lastRunAt: null, cutoffAt: null, refreshed: 0, total: 0 };
  }

  const parsedWithIndex = observedAts
    .map((s, index) => ({ s, ts: Date.parse(s), index }))
    .filter(({ ts }) => !Number.isNaN(ts));

  if (parsedWithIndex.length === 0) {
    return { lastRunAt: null, cutoffAt: null, refreshed: 0, total };
  }

  // Dedupe by timestamp (keeping one representative string for each unique ts).
  const byTs = new Map<number, string>();
  for (const { s, ts } of parsedWithIndex) {
    if (!byTs.has(ts)) {
      byTs.set(ts, s);
    }
  }

  const uniqueDesc = Array.from(byTs.entries())
    .sort((a, b) => b[0] - a[0]);

  const lastRunAt = uniqueDesc[0][1];

  let cutoffTs = uniqueDesc[0][0];
  for (let i = 1; i < uniqueDesc.length; i++) {
    const prev = uniqueDesc[i - 1][0];
    const curr = uniqueDesc[i][0];
    if (prev - curr <= gapMs) {
      cutoffTs = curr;
    } else {
      break;
    }
  }

  const cutoffEntry = Array.from(byTs.entries()).find(([, s]) => {
    const ts = Date.parse(s);
    return ts === cutoffTs;
  });
  const cutoffAt = cutoffEntry ? cutoffEntry[1] : lastRunAt;

  const refreshed = observedAts.filter((s) => isFromLastRun(s, cutoffAt)).length;

  return { lastRunAt, cutoffAt, refreshed, total };
}

export function isFromLastRun(
  observedAt: string,
  cutoffAt: string | null,
): boolean {
  if (cutoffAt === null) return false;
  const observedTs = Date.parse(observedAt);
  const cutoffTs = Date.parse(cutoffAt);
  if (Number.isNaN(observedTs) || Number.isNaN(cutoffTs)) return false;
  return observedTs >= cutoffTs;
}

export function formatObservationAge(observedAt: string, now: number): string {
  const ts = Date.parse(observedAt);
  if (Number.isNaN(ts)) return '—';

  const ageMs = now - ts;
  if (ageMs < 60_000) return 'now';
  if (ageMs < 60 * 60_000) {
    return `${Math.floor(ageMs / 60_000)}m`;
  }
  if (ageMs < 24 * 60 * 60_000) {
    return `${Math.floor(ageMs / (60 * 60_000))}h`;
  }
  return `${Math.floor(ageMs / (24 * 60 * 60_000))}d`;
}

export function formatObservedAtAbsolute(observedAt: string): string {
  const d = new Date(observedAt);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}
